import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { NetworkContextType, NetworkConfig, ContractAddresses } from '@/types';
import { ETCPOWv2ABI, VirtualNodeNFTABI, UpgradeNFTABI, ChestNFTABI, GameLogicV2ABI, RewardsPoolABI, AchievementsV2ABI, LeaderboardABI } from '@/utils/contracts-v2';

const NETWORKS: Record<string, NetworkConfig> = {
  mordor: {
    chainId: 63,
    rpcUrl: 'https://rpc.mordor.etccooperative.org',
    name: 'ETC Mordor Testnet',
    explorer: 'https://blockscout.com/etc/mordor',
    nativeCurrency: {
      name: 'Mordor ETC',
      symbol: 'METC',
      decimals: 18,
    },
  },
  mainnet: {
    chainId: 61,
    rpcUrl: 'https://etc.rivet.link',
    name: 'Ethereum Classic',
    explorer: 'https://blockscout.com/etc/mainnet',
    nativeCurrency: {
      name: 'Ethereum Classic',
      symbol: 'ETC',
      decimals: 18,
    },
  },
};

const ZERO = '0x0000000000000000000000000000000000000000';
const isDeployedAddress = (addr?: string) => Boolean(addr && addr !== ZERO);

/** Prefer network-scoped env vars so Mordor addresses never leak into mainnet builds. */
const addr = (specific: string | undefined, generic: string | undefined, fallback: string) =>
  specific || generic || fallback;

const CONTRACT_ADDRESSES: Record<string, ContractAddresses> = {
  mordor: {
    etcpowv2Token: addr(
      import.meta.env.VITE_MORDOR_ETCPOWV2_TOKEN_ADDRESS,
      import.meta.env.VITE_ETCPOWV2_TOKEN_ADDRESS,
      '0x6856FE43e43996830526cEd56BbC53f3F6a8E80E'
    ),
    nodeNFT: addr(
      import.meta.env.VITE_MORDOR_NODE_NFT_ADDRESS,
      import.meta.env.VITE_NODE_NFT_ADDRESS,
      '0xb4e9Bf27eD19507F2408e954f64c455e748662c7'
    ),
    upgradeNFT: addr(
      import.meta.env.VITE_MORDOR_UPGRADE_NFT_ADDRESS,
      import.meta.env.VITE_UPGRADE_NFT_ADDRESS,
      '0xa33016392c0Fd1F21E1a554D68e080A30B1AB1FA'
    ),
    chestNFT: addr(
      import.meta.env.VITE_MORDOR_CHEST_NFT_ADDRESS,
      import.meta.env.VITE_CHEST_NFT_ADDRESS,
      '0xde99E8653b6cC5b0EaF1ea25Fc48eB376a321448'
    ),
    gameLogic: addr(
      import.meta.env.VITE_MORDOR_GAME_LOGIC_ADDRESS,
      import.meta.env.VITE_GAME_LOGIC_ADDRESS,
      '0x12B82036e4b9f99926fCaE99fB27dA37b0Bd0b19'
    ),
    rewardsPool: addr(
      import.meta.env.VITE_MORDOR_REWARDS_POOL_ADDRESS,
      import.meta.env.VITE_REWARDS_POOL_ADDRESS,
      '0xA35a81976dfB69De132158d73406E79d6aA03C07'
    ),
    achievements: addr(
      import.meta.env.VITE_MORDOR_ACHIEVEMENTS_ADDRESS,
      import.meta.env.VITE_ACHIEVEMENTS_ADDRESS,
      '0x752Ad0174EF1c981c0EE76aFCd0b7AC92C87052d'
    ),
    leaderboard: addr(
      import.meta.env.VITE_MORDOR_LEADERBOARD_ADDRESS,
      import.meta.env.VITE_LEADERBOARD_ADDRESS,
      '0xA1D80211970Acf12ec2A12Ad3fEdE1C93B965d45'
    ),
  },
  mainnet: {
    // Mainnet never inherits generic VITE_* (those are Mordor test defaults in .env)
    etcpowv2Token:
      import.meta.env.VITE_MAINNET_ETCPOWV2_TOKEN_ADDRESS ||
      '0xDe9016EC50335711F32378d730e9f6096B850479',
    nodeNFT: import.meta.env.VITE_MAINNET_NODE_NFT_ADDRESS || ZERO,
    upgradeNFT: import.meta.env.VITE_MAINNET_UPGRADE_NFT_ADDRESS || ZERO,
    chestNFT: import.meta.env.VITE_MAINNET_CHEST_NFT_ADDRESS || ZERO,
    gameLogic: import.meta.env.VITE_MAINNET_GAME_LOGIC_ADDRESS || ZERO,
    rewardsPool: import.meta.env.VITE_MAINNET_REWARDS_POOL_ADDRESS || ZERO,
    achievements: import.meta.env.VITE_MAINNET_ACHIEVEMENTS_ADDRESS || ZERO,
    leaderboard: import.meta.env.VITE_MAINNET_LEADERBOARD_ADDRESS || ZERO,
  },
};

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within NetworkProvider');
  }
  return context;
};

export const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const networkKey = (import.meta.env.VITE_NETWORK || 'mordor').toLowerCase();
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [contracts, setContracts] = useState<any>({});
  const [currentNetwork, setCurrentNetwork] = useState<string>(networkKey);
  const [isConnected, setIsConnected] = useState(false);
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);

  const targetNetwork = NETWORKS[networkKey] || NETWORKS.mordor;
  const addresses = CONTRACT_ADDRESSES[networkKey] || CONTRACT_ADDRESSES.mordor;
  const contractsReady = isDeployedAddress(addresses.gameLogic) && isDeployedAddress(addresses.nodeNFT);

  useEffect(() => {
    if (networkKey === 'mainnet' && !contractsReady) {
      toast.error('ETC mainnet game contracts are not deployed yet. Use Mordor (VITE_NETWORK=mordor).');
    }
    initializeProvider(false);
    setupEventListeners();
    
    return () => {
      cleanupEventListeners();
    };
  }, [currentNetwork]);

  const setupEventListeners = () => {
    if (typeof window.ethereum !== 'undefined') {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }
  };

  const cleanupEventListeners = () => {
    if (typeof window.ethereum !== 'undefined') {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    }
  };

  const handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      disconnectWallet();
      toast.error('Wallet disconnected');
    } else {
      toast.success('Account changed');
      initializeProvider(false);
    }
  };

  const handleChainChanged = (chainId: string) => {
    const currentChainId = parseInt(chainId, 16);
    
    if (currentChainId !== targetNetwork.chainId) {
      setIsWrongNetwork(true);
      toast.error(`Wrong network. Switch to ${targetNetwork.name}.`);
    } else {
      setIsWrongNetwork(false);
      toast.success(`Connected to ${targetNetwork.name}`);
      setCurrentNetwork(networkKey);
      initializeProvider(false);
    }
  };

  const initializeProvider = async (requestSwitch: boolean) => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const browserProvider = new ethers.BrowserProvider(window.ethereum);
        setProvider(browserProvider);

        const accounts: string[] = await window.ethereum.request({ method: 'eth_accounts' });
        if (!accounts || accounts.length === 0) {
          setIsConnected(false);
          setSigner(null);
          setAddress(null);
          setIsWrongNetwork(false);
          return;
        }

        const network = await browserProvider.getNetwork();
        const onTarget = Number(network.chainId) === targetNetwork.chainId;
        setIsWrongNetwork(!onTarget);

        if (!onTarget && requestSwitch) {
          await switchNetwork();
        }

        if (!onTarget && !requestSwitch) {
          setIsConnected(true);
          const signerInstance = await browserProvider.getSigner();
          setSigner(signerInstance);
          setAddress(await signerInstance.getAddress());
          setContracts({});
          return;
        }

        const signerInstance = await browserProvider.getSigner();
        const userAddress = await signerInstance.getAddress();
        setSigner(signerInstance);
        setAddress(userAddress);
        setIsConnected(true);
        
        if (contractsReady) {
          initializeContracts(signerInstance);
        } else {
          setContracts({});
          if (networkKey === 'mainnet') {
            toast.error('Mainnet contracts missing — stay on Mordor for testing.');
          }
        }
      } catch (error) {
        console.error('Failed to initialize provider:', error);
        setIsConnected(false);
      }
    } else {
      setIsConnected(false);
    }
  };

  const initializeContracts = (signerInstance: ethers.Signer) => {
    const addrs = CONTRACT_ADDRESSES[networkKey] || CONTRACT_ADDRESSES.mordor;
    const next: any = {};
    
    if (isDeployedAddress(addrs.etcpowv2Token)) {
      next.etcpowv2Token = new ethers.Contract(addrs.etcpowv2Token, ETCPOWv2ABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.nodeNFT)) {
      next.nodeNFT = new ethers.Contract(addrs.nodeNFT, VirtualNodeNFTABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.upgradeNFT)) {
      next.upgradeNFT = new ethers.Contract(addrs.upgradeNFT, UpgradeNFTABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.chestNFT)) {
      next.chestNFT = new ethers.Contract(addrs.chestNFT, ChestNFTABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.gameLogic)) {
      next.gameLogic = new ethers.Contract(addrs.gameLogic, GameLogicV2ABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.rewardsPool)) {
      next.rewardsPool = new ethers.Contract(addrs.rewardsPool, RewardsPoolABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.achievements)) {
      next.achievements = new ethers.Contract(addrs.achievements, AchievementsV2ABI, signerInstance);
    }
    
    if (isDeployedAddress(addrs.leaderboard)) {
      next.leaderboard = new ethers.Contract(addrs.leaderboard, LeaderboardABI, signerInstance);
    }
    
    setContracts(next);
  };

  const connectWallet = useCallback(async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        if (networkKey === 'mainnet' && !contractsReady) {
          toast.error('ETC mainnet game is not deployed yet. Set VITE_NETWORK=mordor.');
          return;
        }
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        await initializeProvider(true);
        toast.success('Wallet connected');
      } catch (error) {
        console.error('Failed to connect wallet:', error);
        toast.error('Failed to connect wallet');
      }
    } else {
      toast.error('MetaMask not detected. Please install MetaMask.');
    }
  }, [networkKey, contractsReady]);

  const switchNetwork = useCallback(async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: ethers.toBeHex(targetNetwork.chainId) }],
        });
        setIsWrongNetwork(false);
      } catch (error: any) {
        if (error.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: ethers.toBeHex(targetNetwork.chainId),
              chainName: targetNetwork.name,
              rpcUrls: [targetNetwork.rpcUrl],
              blockExplorerUrls: [targetNetwork.explorer],
              nativeCurrency: targetNetwork.nativeCurrency,
            }],
          });
          setIsWrongNetwork(false);
        } else {
          console.error('Failed to switch network:', error);
          toast.error(`Switch wallet to ${targetNetwork.name}`);
        }
      }
    }
  }, [targetNetwork]);

  const disconnectWallet = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
    setContracts({});
    setIsConnected(false);
    setIsWrongNetwork(false);
    toast.success('Wallet disconnected');
  }, []);

  const contextValue = useMemo<NetworkContextType>(
    () => ({
      provider,
      signer,
      address,
      contracts,
      currentNetwork,
      isConnected,
      isWrongNetwork,
      targetNetworkName: targetNetwork.name,
      contractsReady,
      connectWallet,
      switchNetwork,
      disconnectWallet,
    }),
    [
      provider,
      signer,
      address,
      contracts,
      currentNetwork,
      isConnected,
      isWrongNetwork,
      targetNetwork.name,
      contractsReady,
      connectWallet,
      switchNetwork,
      disconnectWallet,
    ]
  );

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  );
};

